﻿import {DateTime, translation} from "@2d-front-monorepo/core";
import {MaterialIcon, Message, PopOver} from "@2d-front-monorepo/design-library";
import {
	AddressComponent,
	Attachments,
	CustomData,
	Drivers,
	ExpiredAttachmentsComponent, getHiddenResidencyProperties, GroupedPermit,
	PermitStatusComponent,
	PermitStatusType, Residencies, ResidencyUses, Tariffs, Vehicles,
	Zones
} from "@2d-front-monorepo/permits-business";
import {PermitTranslations} from "@2d-front-monorepo/shared-translations";

import {JSX, ReactNode, useMemo} from "react";
import {Link} from "react-router";
import {API_ENDPOINT} from "../../../api-endpoint";
import {FilteredItemsList} from "../../../components/Filters/FilteredItemsList";
import {FilteredItemsProvider} from "../../../components/Filters/FilteredItemsProvider";
import {PermitsFilterComponent} from "../../../components/Filters/PermitsFilterComponent";
import Loader from "../../../components/Loader";
import {PermitsState} from "../../../contexts/PermitsContext";
import Registrations, {DefaultPermitProps} from "../../../customers/Registrations";
import {formatDate, formatTime} from "../../../functions";
import {useGlobalContext} from "../../../hooks/useGlobalContext";
import {formatRoute, PrivateRoutes} from "../../../routes";
import {SentToReviewComponent} from "./SentToReviewComponent";
import {TotalComponent} from "./TotalComponent";

type PermitProps = DefaultPermitProps & {
	menu?: ReactNode;
	footer?: ReactNode;
}

const filesPrefix = API_ENDPOINT.fileDownload;

function Permit(props: PermitProps): JSX.Element {
	const {group, driver, residency, vehicles, tariff, zone, menu, footer} = props;
	const permit = group.permit;
	const hasChangeResidency = residency?.changed != null;
	const {installation} = useGlobalContext();
	const hasChangeVehicle = useMemo(() => vehicles.some(x => x.changed != null), [vehicles]);
	const expired = useMemo(() => Attachments.findExpired([driver?.original.entity, residency?.original.entity, ...(vehicles.map(x => x.original.entity))]), []);

	const canUpdateExpired = permit.status.type === PermitStatusType.RequestMoreInformation && expired.length > 0;
	const canSubmitForReview = permit.status.type === PermitStatusType.RequestMoreInformation && expired.length === 0;
	const grouped = group.count > 1;
	const isMulti = CustomData.isMultiOperation(permit);
	const klass = useMemo(() => "card" + (grouped || isMulti ? " card--stacked" : ""), [grouped, isMulti]);
	const notes = grouped ? CustomData.getNotes(permit) : undefined;

	const hiddenResidencyProperties = getHiddenResidencyProperties(installation, residency?.original?.entity?.uses ?? ResidencyUses.None);

	//cuando por configuración no se agrupa, grouped siempre devuelve 1, por lo que no sirve para saber si están agrupados/mostrar card
	return (
		<div className="card--stacked-wrapper">
			<section className={klass}>
				<header className="layout horizontal aligned middle" id={"p_" + permit.id}>
					<div className="flex">
						<div className="columns gap">
							<dl className="permit__date col50">
								<dt>{translation(PermitTranslations.start_date)}</dt>
								<dd>
									<span>{formatDate(group.startDate.toDateOnly())}</span>
									<span>{formatTime(group.startDate.toTimeOnly())}</span>
								</dd>
							</dl>
							<dl className="permit__date col50">
								<dt>{translation(PermitTranslations.end_date)}</dt>
								<dd>
									<span>{group.endDate && formatDate(group.endDate.toDateOnly())}</span>
									<span>{group.endDate && formatTime(group.endDate.toTimeOnly())}</span>
								</dd>
							</dl>
						</div>

						{/*<h3>{grouped ? <span>({group.count})</span> : undefined}</h3>*/}
						{notes != null && <small className="notes">{notes}</small>}
					</div>

					{isMulti
						? <div><Loader small={true} /></div>
						: <>
							<PermitStatusComponent permit={permit}/>
							<div><PopOver>{menu}</PopOver></div>
						</>}
				</header>
				<hr className="no-margin"/>
				<div className="columns gap padded">
					<dl className="title-desc col25">
                        <dt>{translation(PermitTranslations.zone)}</dt>
                        <dd>{zone.name || zone.id}</dd>
                    </dl>

					<dl className="title-desc col25">
						<dt>{translation(PermitTranslations.tariff)}</dt>
						<dd>{tariff.title}</dd>
					</dl>

					{residency && <dl className="title-desc col25">
                        <dt>{translation(PermitTranslations.address)}</dt>
                        <dd className="layout">
							{hasChangeResidency &&
                                <MaterialIcon icon="swap_horizontal_circle" className="entity-change--icon"
                                              title={translation(PermitTranslations.change_in_progress)}/>}
                            <div className="flex">
                                <AddressComponent value={residency.original.entity} hiddenEntityProperties={hiddenResidencyProperties}/>
                            </div>
                        </dd>
                    </dl>}

					{vehicles.length > 0 &&
                        <dl className="title-desc col25">
                            <dt>{translation(PermitTranslations.vehicles)}</dt>
                            <dd className="layout">
								{hasChangeVehicle &&
                                    <MaterialIcon icon="swap_horizontal_circle" className="entity-change--icon"
                                                  title={translation(PermitTranslations.change_in_progress)}/>}
                                <div className="flex">
									{vehicles.map(((v, i) => <span key={i}>{v.original.entity.plate}</span>))}
                                </div>
                            </dd>
                        </dl>}
					{footer}
					{canUpdateExpired &&
                        <ExpiredAttachmentsComponent
                            attachments={expired}
                            linkPrefix={filesPrefix} className="col100">
                            <div>
                                <Link
                                    className="entity-documentation-update form__button form__button--primary form__button--small"
                                    to={formatRoute(PrivateRoutes.permitsDocumentationUpdate, permit.id)}>{translation(PermitTranslations.documentation__update__button)}</Link>
                            </div>
                        </ExpiredAttachmentsComponent>
					}
					{canSubmitForReview && <SentToReviewComponent permitId={permit.id}/>}
				</div>

				<TotalComponent permit={permit}/>
			</section>
		</div>);
}

export default function PermitListComponent({permits, holderData, now, grouped}: PermitsState & {
	now: DateTime,
	grouped: GroupedPermit[],
}): JSX.Element {
	const MenuComponent = Registrations.getPermitMenu();
	const FooterComponent = Registrations.getPermitFooter();

	if (grouped.length === 0) {
		return <Message title={{text: translation(PermitTranslations.summary_page__no_permits)}} type="information"/>;
	}

	return <div className="layout vertical gap">
		<FilteredItemsProvider items={grouped}>
			<PermitsFilterComponent/>
			<FilteredItemsList
				keyExtractor={(group: GroupedPermit) => group.permit.id}
				renderItem={(group: GroupedPermit) => {
					const permit = group.permit;
					const tariff = Tariffs.get(holderData, permit.tariffId);
					const zone = Zones.get(holderData, permit.parkingLocation.zoneId);
					const driver = Drivers.getChanged(permits, permit.driver);
					const residency = Residencies.getChanged(permits, permit.residency);
					const vehicles = Vehicles.getChanged(permits, permit.vehicles);

					return <Permit key={permit.id}
					               group={group}
					               permits={permits}
					               holderData={holderData}
					               driver={driver}
					               residency={residency}
					               vehicles={vehicles}
					               zone={zone}
					               tariff={tariff}
					               now={now}
					               footer={<FooterComponent group={group}
					                                        permits={permits}
					                                        holderData={holderData}
					                                        driver={driver}
					                                        residency={residency}
					                                        vehicles={vehicles}
					                                        zone={zone}
					                                        tariff={tariff}
					                                        now={now}/>}
					               menu={<MenuComponent group={group}
					                                    permits={permits}
					                                    holderData={holderData}
					                                    driver={driver}
					                                    residency={residency}
					                                    vehicles={vehicles}
					                                    zone={zone}
					                                    tariff={tariff}
					                                    now={now}/>}
					/>
				}}/>
		</FilteredItemsProvider>
	</div>;
}
